import {
    PasswordPolicyValidator,
    type PolicyValidatorArgs,
    type PolicyValidatorCollector,
    type PolicyValidatorRuleNames,
    UsernamePolicyValidator,
} from '@cybusio/policy-validator'

import type { PartialReadonlyRecord } from '../../../utils'

import { ConnectwareError, ConnectwareErrorType, Translation } from '../../../domain'

import type { TranslationService } from '../../../application'

import type { BackendJsonResponseContent } from '../../Connectware'

type ValidationPolicy<RuleName extends string> = PartialReadonlyRecord<RuleName, number>

class PolicyValidator<RuleName extends string> {
    constructor (
        private readonly ValidatorConstructor: new <Result>(args: PolicyValidatorArgs<RuleName, Result>) => { validate(input: string): Result },
        private readonly translationService: TranslationService,
        private readonly translation: Translation
    ) {}

    private createCollector (): PolicyValidatorCollector<RuleName, readonly (string | ConnectwareError)[]> {
        const values: (string | ConnectwareError)[] = []

        return {
            add: ({ name, threshold, valid }) => {
                if (threshold === 0) {
                    /** We don't care about these scenarios */
                    return
                }

                const message = this.translationService.translate(this.translation, { type: name, count: threshold })

                values.push(valid ? message : new ConnectwareError(ConnectwareErrorType.GENERAL_BUSINESS_RULE_INFRACTION, message))
            },
            values,
        }
    }

    async validate (policyPromise: Promise<ValidationPolicy<RuleName>>, input: string): Promise<readonly (string | ConnectwareError)[]> {
        return new this.ValidatorConstructor({ thresholds: await policyPromise, createCollector: () => this.createCollector() }).validate(input)
    }
}

type Before = ValidationPolicy<'min'>
type After = ValidationPolicy<'minLength'>
const policyPromiseTransformer = <In extends Before>({ min, ...value }: In): Omit<In, keyof Before> & After => ({ ...value, minLength: min })

/**
 * Utility class to centralize username and password validation
 */
export class PoliciesValidator {
    private readonly usernameValidator: PolicyValidator<PolicyValidatorRuleNames<UsernamePolicyValidator>>
    private readonly passwordValidator: PolicyValidator<PolicyValidatorRuleNames<PasswordPolicyValidator>>

    constructor (translationService: TranslationService) {
        this.usernameValidator = new PolicyValidator(UsernamePolicyValidator, translationService, Translation.USERNAME_VALIDATION)
        this.passwordValidator = new PolicyValidator(PasswordPolicyValidator, translationService, Translation.PASSWORD_VALIDATION)
    }

    validatePassword (
        policy: Promise<BackendJsonResponseContent<'/api/policy/password', 'GET', 200>>,
        password: string
    ): Promise<readonly (string | ConnectwareError)[]> {
        return this.passwordValidator.validate(policy.then(policyPromiseTransformer), password)
    }

    validateUsername (
        policy: Promise<BackendJsonResponseContent<'/api/policy/username', 'GET', 200>>,
        username: string
    ): Promise<readonly (string | ConnectwareError)[]> {
        return this.usernameValidator.validate(policy.then(policyPromiseTransformer), username)
    }
}
